import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, Search, X, ChevronRight, Users, Calendar, FileText, Settings, Menu, AlertTriangle, Clock,
  CalendarOff, LogOut, User as UserIcon, LayoutDashboard, HeartPulse, HeartHandshake, Compass, Wallet,
  BarChart3, MessageSquare, KeyRound,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavIndex, searchNav } from './navIndex';
import { hueFor, isSchedulingRoute } from './routes';
import useAttention from '../../hooks/useAttention';

// The badge's icon when the page's own sidebar icon isn't known.
const HUE_ICON = {
  indigo: LayoutDashboard, orange: Users, pink: HeartPulse, teal: HeartHandshake, violet: Compass,
  sky: FileText, emerald: Wallet, amber: BarChart3, cyan: MessageSquare, slate: Settings, blue: Calendar,
};

// Page titles for the top bar. Every page outside Scheduling also falls back to its sidebar label
// (the self-registering nav index), so no page is left saying just "Rostery".
const TITLES = {
  '/':                          { title: 'Dashboard',          sub: 'Overview of operations' },
  '/clients':                   { title: 'Clients',            sub: 'Manage participant profiles' },
  '/clients/new':               { title: 'New client',         sub: 'Add a participant' },
  '/clients/archived':          { title: 'Archived clients',   sub: 'Participants who are no longer active' },
  '/staff':                     { title: 'Staff',              sub: 'Manage your team' },
  '/staff/new':                 { title: 'New staff member',   sub: 'Add someone to your team' },
  '/staff/archived':            { title: 'Archived staff',     sub: 'People who no longer work here' },
  '/staff/teams':               { title: 'Staff Teams',        sub: 'Team groupings & assignments' },
  '/staff/contracts':           { title: 'Contracts',          sub: 'Employment contracts and signatures' },
  '/staff/documents':           { title: 'Staff documents',    sub: 'Checks, certificates and shared files' },
  '/teams':                     { title: 'Teams',              sub: 'Group staff for rostering and reporting' },
  '/scheduler':                 { title: 'Scheduler',          sub: 'Manage shifts & rosters' },
  '/timesheets':                { title: 'Timesheets',         sub: 'Review & approve attendance' },
  '/invoices':                  { title: 'Invoices',           sub: 'Billing & payment tracking' },
  '/payslips':                  { title: 'Payslips',           sub: 'Earnings statements from approved timesheets' },
  '/reimbursements':            { title: 'Reimbursements',     sub: 'Expense claims from staff' },
  '/mileage':                   { title: 'Mileage',            sub: 'Trips, reimbursements and ATO log' },
  '/financials':                { title: 'Financials',         sub: 'Revenue, cost and profit' },
  '/financials/client-rates':   { title: 'Client rates',       sub: 'Charge rates used for margin reports' },
  '/financials/staff-rates':    { title: 'Staff rates',        sub: 'Cost rates used for margin reports' },
  '/financials/config':         { title: 'Financial configuration', sub: 'Multipliers, owners, super and period close' },
  '/financials/pay-contractors':{ title: 'Pay contractors',    sub: 'Contractor invoices and bank files' },
  '/financials/travel-transport':{ title: 'Travel & transport', sub: 'Mileage and transport charges' },
  '/financials/expenses':       { title: 'Expenses',           sub: 'Business expenses' },
  '/financials/reports':        { title: 'Financial reports',  sub: 'Profit, pay, funding and audit' },
  '/billing':                   { title: 'Billing & plan',     sub: 'Your Rostery subscription' },
  '/incidents':                 { title: 'Incidents',          sub: 'Safety & incident management' },
  '/tasks':                     { title: 'Tasks',              sub: 'Track operational tasks' },
  '/reports':                   { title: 'Reports',            sub: 'Analytics & compliance' },
  '/medications':               { title: 'Medications',        sub: 'eMAR — medication management' },
  '/forms':                     { title: 'Forms',              sub: 'Custom forms & submissions' },
  '/messages':                  { title: 'Messages',           sub: 'Team communication' },
  '/leave':                     { title: 'Leave',              sub: 'Leave requests & approvals' },
  '/job-board':                 { title: 'Job Board',          sub: 'Open & vacant shifts' },
  '/facilities':                { title: 'Facilities',         sub: 'Locations & sites' },
  '/service-agreements':        { title: 'Service agreements', sub: 'NDIS service agreements with participants' },
  '/vehicles':                  { title: 'Fleet',              sub: 'Company vehicles, rego and servicing' },
  '/automations':               { title: 'Reminders & reviews', sub: 'No-show reminders and review requests by SMS' },
  '/activity':                  { title: 'Activity log',       sub: 'Who changed what, and when' },
  '/ai-receptionist':           { title: 'AI receptionist',    sub: 'Calls, leads and callbacks' },
  '/receptionist':              { title: 'AI receptionist',    sub: 'Calls, leads and callbacks' },
  '/chatbot':                   { title: 'Website chat',       sub: 'Chat widget for your website' },
  '/migration':                 { title: 'Data import',        sub: 'Bring records in from another system' },
  '/export-data':               { title: 'Export data',        sub: 'Download your organisation’s data' },
  '/aged-care':                 { title: 'Aged Care',          sub: 'Support at Home and CHSP' },
  '/support-coordination':      { title: 'Support Coordination', sub: 'Caseload, notes and budgets' },
  '/plan-management':           { title: 'Plan Management',    sub: 'Invoices, claims and payments' },
  '/settings':                  { title: 'Settings',           sub: 'Configure your workspace' },
};

// Scheduling screens get no sidebar fallback (see fromNav below), so each needs a title here or
// the header just says "Rostery". Kept out of TITLES so search does not offer them to an org
// whose plan hides them: search lists these through the sidebar's gated nav index instead.
const SCHEDULING_TITLES = {
  '/templates':                 { title: 'Templates',          sub: 'Reusable shift patterns' },
  '/groups':                    { title: 'Group Services',     sub: 'Group activities and attendance' },
  '/live-tracking':             { title: 'Tracking',           sub: 'Live locations and shift routes' },
  '/worker-activity':           { title: 'Tracking',           sub: 'Live locations and shift routes' },
  '/evv':                       { title: 'EVV',                sub: 'Electronic visit verification' },
};

const QUICK_LINKS = [
  { label: 'Clients',   path: '/clients',   icon: Users,    hint: 'View all participants' },
  { label: 'Scheduler', path: '/scheduler', icon: Calendar, hint: 'Manage shifts' },
  { label: 'Invoices',  path: '/invoices',  icon: FileText, hint: 'Billing & payments' },
  { label: 'Settings',  path: '/settings',  icon: Settings, hint: 'Configure workspace' },
];

const TITLE_PAGES = Object.entries(TITLES).map(([path, t]) => ({ path, label: t.title, section: t.sub }));
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

const SEVERITY = {
  danger:  { icon: AlertTriangle, tile: 'bg-red-50 text-red-600' },
  warning: { icon: Clock,         tile: 'bg-amber-50 text-amber-600' },
  info:    { icon: CalendarOff,   tile: 'bg-slate-100 text-slate-600' },
};
const ATTENTION_ICON = { incidents: AlertTriangle, timesheets: Clock, leave: CalendarOff };

function SearchModal({ onClose }) {
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const nav = useNavIndex();

  useEffect(() => {
    const back = document.activeElement;
    inputRef.current?.focus();
    // Load searchable records once so typing actually finds clients & staff.
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || []))).catch(() => {});
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : (r.data?.staff || []))).catch(() => {});
    return () => back?.focus?.({ preventScroll: true });
  }, []);

  const q = query.trim().toLowerCase();
  const nameOf = (o) => `${o.first_name || ''} ${o.last_name || ''}`.trim();

  // Pages come from the same index the sidebar registers (every screen this org can open), plus
  // the titled pages above, de-duplicated by path.
  const pagePool = useMemo(() => {
    const seen = new Map();
    for (const e of (nav?.all || [])) seen.set(e.path, { path: e.path, label: e.label, section: e.section });
    for (const e of TITLE_PAGES) if (!seen.has(e.path)) seen.set(e.path, e);
    return Array.from(seen.values());
  }, [nav?.all]);

  const results = useMemo(() => {
    if (!q) return [{ group: 'Go to', items: QUICK_LINKS.map((l) => ({ key: l.path, to: l.path, title: l.label, sub: l.hint, icon: l.icon })) }];
    const groups = [];
    const cl = clients.filter(c => nameOf(c).toLowerCase().includes(q) || (c.ndis_number || '').toLowerCase().includes(q)).slice(0, 6);
    if (cl.length) groups.push({ group: 'Clients', items: cl.map(c => ({ key: `c${c.id}`, to: `/clients/${c.id}`, title: nameOf(c), sub: c.ndis_number ? `NDIS ${c.ndis_number}` : 'Participant', initials: nameOf(c) })) });
    const st = staff.filter(s => nameOf(s).toLowerCase().includes(q)).slice(0, 6);
    if (st.length) groups.push({ group: 'Staff', items: st.map(s => ({ key: `s${s.id}`, to: `/staff/${s.id}`, title: nameOf(s), sub: (s.role || '').replace(/_/g, ' '), initials: nameOf(s) })) });
    const pg = searchNav(pagePool, q).slice(0, 8);
    if (pg.length) groups.push({ group: 'Pages', items: pg.map(p => ({ key: `p${p.path}`, to: p.path, title: p.label, sub: p.section, icon: Search })) });
    return groups;
  }, [q, clients, staff, pagePool]);

  const flat = useMemo(() => results.flatMap((g) => g.items), [results]);
  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = (item) => { if (!item) return; navigate(item.to); onClose(); };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[active]); }
  };

  let idx = -1;
  return (
    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: .12 }} onMouseDown={onClose}>
      <motion.div
        role="dialog" aria-modal="true" aria-label="Search Rostery"
        className="bg-white rounded-2xl w-full max-w-xl overflow-hidden mx-auto self-start mt-[12vh] shadow-modal"
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
        transition={{ duration: .15, ease: [.23, 1, .32, 1] }}
        onMouseDown={e => e.stopPropagation()} onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100">
          <Search size={17} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search pages, clients, staff…" aria-label="Search pages, clients and staff"
            role="combobox" aria-expanded="true" aria-controls="palette-results"
            aria-activedescendant={flat[active] ? `palette-${active}` : undefined}
            className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 outline-none bg-transparent"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="p-1 rounded-lg text-slate-400 hover:text-slate-600 transition-colors">
              <X size={14} />
            </button>
          )}
        </div>

        <div ref={listRef} id="palette-results" role="listbox" aria-label="Results" className="p-2 max-h-[60vh] overflow-auto">
          {q && flat.length === 0 ? (
            <p className="px-3 py-10 text-center text-sm text-slate-500">No results for “{query}”. Try a name, an NDIS number or a page.</p>
          ) : results.map((g) => (
            <div key={g.group} className="mb-2 last:mb-0">
              <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold text-slate-500">{g.group}</p>
              {g.items.map((it) => {
                idx += 1;
                const i = idx;
                const on = i === active;
                const Icon = it.icon;
                return (
                  <button key={it.key} id={`palette-${i}`} data-idx={i} type="button" role="option" aria-selected={on}
                    onMouseMove={() => setActive(i)} onClick={() => go(it)}
                    className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-left transition-colors ${on ? 'bg-slate-100' : ''}`}>
                    {it.initials ? (
                      <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold shrink-0">
                        {it.initials.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    ) : (
                      <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon size={15} className="text-slate-500" /></span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-slate-800 truncate">{it.title}</span>
                      {it.sub && <span className="block text-xs text-slate-500 truncate">{it.sub}</span>}
                    </span>
                    <ChevronRight size={13} className={on ? 'text-slate-500' : 'text-slate-300'} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/80 flex items-center gap-4 text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5"><kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono">↑↓</kbd> move</span>
          <span className="flex items-center gap-1.5"><kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono">Enter</kbd> open</span>
          <span className="flex items-center gap-1.5"><kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono">Esc</kbd> close</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

function AccountMenu({ user, onSignOut }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || (user?.name || '?').slice(0, 2).toUpperCase();
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.name || 'Account';
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
        className="flex items-center gap-2.5 rounded-xl px-1.5 py-1 hover:bg-slate-100 transition-colors">
        <span className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{initials}</span>
        <span className="hidden sm:block leading-none text-left">
          <span className="block text-sm font-semibold text-slate-700">{user?.firstName || user?.name}</span>
          <span className="block text-[11px] text-slate-500 capitalize mt-0.5">{(user?.role || '').replace(/_/g, ' ')}</span>
        </span>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-card-deep py-1.5 z-30 animate-fade-in motion-reduce:animate-none">
          <div className="px-3.5 py-2.5 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-800 truncate">{name}</p>
            {user?.email && <p className="text-xs text-slate-500 truncate">{user.email}</p>}
            {user?.orgName && <p className="text-xs text-slate-500 truncate mt-0.5">{user.orgName}</p>}
          </div>
          {user?.id && !user?.impersonating && user?.role !== 'super_admin' && (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); navigate(`/staff/${user.id}`); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-50 text-left">
              <UserIcon size={14} className="text-slate-400" /> My profile
            </button>
          )}
          {!user?.impersonating && (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); navigate('/account/security'); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-50 text-left">
              <KeyRound size={14} className="text-slate-400" /> Security &amp; two-factor
            </button>
          )}
          <button type="button" role="menuitem" onClick={onSignOut}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-50 text-left">
            <LogOut size={14} className="text-slate-400" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Header({ setMobileOpen }) {
  const { pathname, search } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = useNavIndex();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen]   = useState(false);
  const { items: attention } = useAttention();

  const scheduling = isSchedulingRoute(pathname);
  // TITLES is matched by prefix, so a record's edit screen needs its own pattern.
  const clientEdit = /^\/clients\/[^/]+\/edit$/.test(pathname) ? { title: 'Edit client', sub: 'Update participant details' } : null;
  // Email's screens are views of /mail (?view=calendar, tasks, settings).
  const emailTitle = pathname !== '/mail' ? null : ({
    calendar: { title: 'Calendar', sub: 'Email · the company calendar' },
    tasks: { title: 'Tasks', sub: 'Email · office and SC tasks' },
    settings: { title: 'Email settings', sub: 'Email' },
  }[new URLSearchParams(search).get('view')] || { title: 'Email', sub: 'Mail from every mailbox' });
  const fromTitles = emailTitle || clientEdit || Object.entries({ ...TITLES, ...SCHEDULING_TITLES })
    .filter(([k]) => pathname === k || pathname.startsWith(k + '/'))
    .sort((a, b) => b[0].length - a[0].length)[0]?.[1];
  // Outside Scheduling, fall back to the sidebar's own label for the page.
  const fromNav = !fromTitles && !scheduling
    ? (nav?.all || []).filter((e) => e.path !== '/' && (pathname === e.path || pathname.startsWith(e.path + '/')))
        .sort((a, b) => b.path.length - a.path.length)[0]
    : null;
  const info = fromTitles || (fromNav ? { title: fromNav.label, sub: fromNav.section } : null) || { title: 'Rostery', sub: '' };
  // The section's colour badge (not on Scheduling, which keeps its header as it was).
  const hue = hueFor(pathname);
  const navIcon = pathname === '/' ? LayoutDashboard : (nav?.all || [])
    .filter((e) => e.icon && e.path !== '/' && (pathname === e.path || pathname.startsWith(e.path + '/')))
    .sort((a, b) => b.path.length - a.path.length)[0]?.icon;
  const BadgeIcon = navIcon || HUE_ICON[hue] || LayoutDashboard;

  const today = new Date().toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  // Ctrl+K / Cmd+K opens search
  useEffect(() => {
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(s => !s);
      }
    };
    // Other screens (e.g. the 404 page's "Search Rostery") open it with this event.
    const open = () => setSearchOpen(true);
    window.addEventListener('keydown', handler);
    window.addEventListener('rostery:open-search', open);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('rostery:open-search', open);
    };
  }, []);

  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const signOut = async () => { await logout(); navigate('/login'); };

  return (
    <>
      <header className="h-16 sticky top-0 z-20 flex items-center px-5 gap-3 print:hidden"
        style={{ background: 'rgba(255,255,255,.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(226,232,240,.7)' }}>

        {/* Hamburger — mobile only */}
        <button
          type="button"
          onClick={() => setMobileOpen?.(o => !o)}
          className="md:hidden p-2 -ml-1 rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>

        {/* Page info */}
        {!scheduling && (
          <span className={`hue-${hue} hue-solid hidden sm:flex w-9 h-9 rounded-xl items-center justify-center shrink-0 shadow-sm`} aria-hidden="true">
            <BadgeIcon size={18} />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-slate-900 leading-none truncate">{info.title}</h1>
          {info.sub && <p className="text-xs text-slate-500 mt-1 hidden sm:block truncate">{info.sub}</p>}
        </div>

        {/* Search trigger */}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="hidden md:flex items-center gap-2.5 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50/80
                     text-sm text-slate-500 hover:border-slate-300 hover:bg-white hover:text-slate-700
                     transition-colors duration-150 min-w-[200px]"
          aria-label="Search Rostery"
        >
          <Search size={14} className="shrink-0" />
          <span className="flex-1 text-left text-xs">Search…</span>
          <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono text-slate-500">{IS_MAC ? '⌘ K' : 'Ctrl K'}</kbd>
        </button>

        {/* Mobile search icon */}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
          className="md:hidden p-2 rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <Search size={17} />
        </button>

        {/* Date */}
        <span className="hidden lg:block text-xs text-slate-500 font-medium shrink-0">{today}</span>

        {/* Notifications — the same "needs you" list the dashboard uses; refreshes on focus and every minute */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setNotifOpen(o => !o)}
            aria-label={attention.length ? `Notifications, ${attention.length} need attention` : 'Notifications'}
            aria-expanded={notifOpen}
            aria-haspopup="true"
            className="relative p-2 rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <Bell size={17} />
            {attention.length > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-4 text-center ring-2 ring-white tabular-nums">
                {attention.length}
              </span>
            )}
          </button>

          <AnimatePresence>
            {notifOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setNotifOpen(false)} />
                <motion.div
                  className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-1rem)] bg-white rounded-2xl border border-slate-200 z-20 shadow-card-deep"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: .15 }}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-800">Needs attention</p>
                  </div>
                  {attention.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-sm text-slate-600">Nothing needs you right now.</p>
                      <p className="text-xs text-slate-500 mt-1">No pending approvals, leave or open incidents.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {attention.map((n) => {
                        const sev = SEVERITY[n.severity] || SEVERITY.info;
                        const Icon = ATTENTION_ICON[n.id] || sev.icon;
                        return (
                          <button key={n.id} type="button" onClick={() => { setNotifOpen(false); navigate(n.to); }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left">
                            <span className={`w-8 h-8 rounded-lg ${sev.tile} flex items-center justify-center shrink-0`}><Icon size={15} /></span>
                            <span className="flex-1 min-w-0 text-[13px] font-medium text-slate-700 leading-snug">{n.label}</span>
                            <ChevronRight size={13} className="text-slate-300 shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Account */}
        <AccountMenu user={user} onSignOut={signOut} />
      </header>

      <AnimatePresence>
        {searchOpen && <SearchModal onClose={closeSearch} />}
      </AnimatePresence>
    </>
  );
}
